/**
 * The middle of the sidebar header when Ask Addy v2 is on: the assistant's picture and name, as
 * the organization has them. What it's doing shows in its dot (and to screen readers), not as
 * text. The picture opens the assistant's profile, where Autopilot lives; the name opens the
 * agent switcher. It also reports the inbox's unread count.
 */
import { useEffect } from "react";
import LendyIcon from "../components/lendy/LendyIcon";
import AssistantAvatar from "./AssistantAvatar";
import { reportInbox, useAgentNews } from "./agentNews";
import { getLendyStatus } from "./headerStatus";
import { getAutopilotState } from "./profile/autopilotFormat";
import useAutopilot from "./profile/useAutopilot";
import "./lendyHeader.css";

export default function LendyHeader({ organizationId, userId, loanId, assistantName, assistantImageUrl, run, isProfileOpen, onOpenProfile, isAgentMenuOpen, onOpenAgentMenu, onUnreadChange, onStatusChange }) {
  const autopilot = useAutopilot({ organizationId, userId, loanId });
  const { inbox } = autopilot;
  const state = getAutopilotState(autopilot);

  useEffect(() => {
    if (!inbox) return;
    onUnreadChange?.(inbox.unread || 0);
    reportInbox(inbox);
  }, [inbox, onUnreadChange]);

  const { hasNews } = useAgentNews();
  const status = getLendyStatus({
    run,
    autopilot: autopilot.isConnected ? state : { text: "", tone: "ready" },
  });

  // The sidebar's rail shows the same dot on the assistant at its foot.
  useEffect(() => {
    onStatusChange?.(status.tone);
  }, [status.tone, onStatusChange]);

  // The organization's default agent hasn't loaded yet (first visit only): a blank face and name.
  if (!assistantName) {
    return (
      <div className="lendy-header-who lendy-header-who--loading" aria-busy="true" aria-label="Loading assistant">
        <span className="lendy-avatar lendy-avatar--center">
          <span className="lendy-avatar-clip" />
        </span>
        <span className="lendy-header-name-placeholder" />
      </div>
    );
  }

  return (
    <div className="lendy-header-who">
      <button
        type="button"
        className="lendy-header-face"
        aria-label={status.text ? `${assistantName}: ${status.text}. Open ${assistantName}'s profile` : `Open ${assistantName}'s profile`}
        aria-expanded={isProfileOpen}
        onClick={onOpenProfile}
      >
        <AssistantAvatar imageUrl={assistantImageUrl} size="center" dot={status.tone} />
        {hasNews && (
          <span className="lendy-header-news lendy-ping" aria-hidden="true">
            <i />
          </span>
        )}
      </button>
      <button
        type="button"
        className="lendy-header-name"
        aria-label={`${assistantName}. Switch agent`}
        aria-haspopup="menu"
        aria-expanded={Boolean(isAgentMenuOpen)}
        onClick={onOpenAgentMenu}
      >
        <span className="lendy-header-name-text">{assistantName}</span>
        <LendyIcon name="down" size={13} />
      </button>
    </div>
  );
}
